// 'use client' is load-bearing: this module is reachable from the
// `@auctogon/ui/web` barrel, and Sonner's toaster subscribes to a store with
// hooks (web-barrel-client-directive.test.ts, BL-1124).
'use client';

/**
 * Toaster — the ONE toast implementation, shared by both apps (BL-1905).
 *
 * Bidder-web's Sonner `AppToaster` was the only toast in the monorepo; admin
 * needed one too. Per ADR 030 — Design Canon (Decision 2) the core owns the
 * behaviour every toast shares — a close button on every toast, the light
 * theme, the circle-alert warning glyph — and each app binds its placement and
 * look through a {@link ToasterDialect}, the same data-not-className-soup seam
 * `Alert` uses. The per-app skins are `apps/bidder-web/src/components/ui/AppToaster.tsx`
 * and `packages/admin-frontend/src/components/ui/AdminToaster.tsx`; neither
 * imports Sonner itself (`Toaster.skins.guard.test.ts`).
 *
 * `toast` is re-exported so callers fire toasts through the design system, not
 * the library: `toast.success('Status set to Upcoming · 3 lots')`.
 */

import type { CSSProperties, ReactNode } from 'react';
import { Toaster as SonnerToaster, toast, type ToasterProps as SonnerToasterProps } from 'sonner';
import { CircleAlert } from 'lucide-react';

export { toast };

/** Where the toast stack anchors — Sonner's six positions. */
export type ToastPosition = NonNullable<SonnerToasterProps['position']>;

/** Per-state icons a dialect may add; unset states keep the shared set. */
export interface ToastIcons {
    success?: ReactNode;
    info?: ReactNode;
    warning?: ReactNode;
    error?: ReactNode;
}

/**
 * One app's toast appearance. Data, not markup: every value here is a look
 * (where the stack sits, how long a toast lives, the classes a toast wears),
 * never a behaviour the core owns.
 */
export interface ToasterDialect {
    /** The default anchor. A skin may override it per render (see `position`). */
    position: ToastPosition;
    /** Space between stacked toasts, in px. */
    gap?: number;
    /** How long a toast stays before closing itself, in ms. */
    duration?: number;
    /** Distance from the anchored edges (a number is px; a string is any CSS length). */
    offset?: SonnerToasterProps['offset'];
    /** Icons this app adds on top of the shared set. */
    icons?: ToastIcons;
    /** Per-toast classes and styling (`unstyled` + Tailwind classes, for admin). */
    toastOptions?: SonnerToasterProps['toastOptions'];
    /** Inline custom properties on the stack container (e.g. Sonner's `--width`). */
    style?: CSSProperties;
}

export interface ToasterProps {
    /** The app's appearance — see {@link ToasterDialect}. */
    dialect: ToasterDialect;
    /**
     * Overrides `dialect.position` for this render. Bidder re-anchors by
     * viewport (BUG-877: top-center below the 1200px desktop switch, so a toast
     * never covers the fixed bottom bid CTAs).
     */
    position?: ToastPosition;
}

/** The warning glyph both apps use: a circle-exclamation, not Sonner's triangle. */
const SHARED_ICONS: ToastIcons = {
    warning: <CircleAlert size={20} strokeWidth={1.8} />,
};

/**
 * The app-wide toast stack. Renders nothing until a `toast()` is fired.
 *
 * @param props See {@link ToasterProps}.
 * @returns The light-themed Sonner stack, every toast carrying a close button.
 */
export function Toaster({ dialect, position }: ToasterProps) {
    return (
        <SonnerToaster
            theme="light"
            closeButton
            position={position ?? dialect.position}
            gap={dialect.gap}
            duration={dialect.duration}
            offset={dialect.offset}
            icons={{ ...SHARED_ICONS, ...dialect.icons }}
            toastOptions={dialect.toastOptions}
            style={dialect.style}
        />
    );
}
